/**
 * ImageCropModal - Profile photo cropping component
 * Allows users to crop and center their profile photo before uploading
 */
import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { ZoomIn, ZoomOut, RotateCw, Check, X } from 'lucide-react';

// Helper function to create cropped image
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    // Don't set crossOrigin for data URLs as it can cause issues
    if (!url.startsWith('data:')) {
      image.setAttribute('crossOrigin', 'anonymous');
    }
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
  try {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    const maxSize = Math.max(image.width, image.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

    canvas.width = safeArea;
    canvas.height = safeArea;

    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-safeArea / 2, -safeArea / 2);

    ctx.drawImage(
      image,
      safeArea / 2 - image.width * 0.5,
      safeArea / 2 - image.height * 0.5
    );

    const data = ctx.getImageData(0, 0, safeArea, safeArea);

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.putImageData(
      data,
      Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
      Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob from canvas'));
        }
      }, 'image/jpeg', 0.9);
    });
  } catch (error) {
    console.error('Error in getCroppedImg:', error);
    throw error;
  }
};

const ImageCropModal = ({ isOpen, onClose, imageSrc, onCropComplete, onSkip }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cropError, setCropError] = useState(false);

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleConfirm = async () => {
    if (!croppedAreaPixels) {
      console.error('No cropped area pixels available');
      return;
    }
    
    setIsProcessing(true);
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      if (!croppedBlob) {
        throw new Error('Failed to create cropped image blob');
      }
      const croppedFile = new File([croppedBlob], 'profile-photo.jpg', { type: 'image/jpeg' });
      onCropComplete(croppedFile);
      onClose();
    } catch (error) {
      console.error('Error cropping image:', error);
      // Show toast error if available
      if (typeof window !== 'undefined') {
        import('sonner').then(({ toast }) => {
          toast.error('Failed to process image. Please try again.');
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden" style={{
        background: 'var(--ps-bg-surface)',
        border: '1px solid var(--ps-border)'
      }}>
        <DialogHeader>
          <DialogTitle className="text-[var(--ps-text-primary)]">
            Adjust Profile Photo
          </DialogTitle>
        </DialogHeader>
        
        <div className="relative w-full" style={{ height: '350px' }}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={onCropChange}
              onZoomChange={onZoomChange}
              onCropComplete={onCropCompleteCallback}
              style={{
                containerStyle: {
                  background: 'var(--ps-bg-base)',
                  borderRadius: '12px',
                },
                cropAreaStyle: {
                  border: '3px solid var(--ps-primary)',
                }
              }}
            />
          )}
        </div>

        {/* Controls */}
        <div className="space-y-4 mt-4">
          {/* Zoom control */}
          <div className="flex items-center gap-3">
            <ZoomOut className="w-5 h-5 text-[var(--ps-text-muted)]" />
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.1}
              onValueChange={(value) => setZoom(value[0])}
              className="flex-1"
            />
            <ZoomIn className="w-5 h-5 text-[var(--ps-text-muted)]" />
          </div>

          {/* Rotate button */}
          <div className="flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRotate}
              className="border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
            >
              <RotateCw className="w-4 h-4 mr-2" />
              Rotate
            </Button>
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 mt-4">
          <div className="flex gap-2 w-full">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="flex-1 border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isProcessing}
              className="flex-1 bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)] text-white"
            >
              {isProcessing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Apply
                </>
              )}
            </Button>
          </div>
          {onSkip && (
            <button
              onClick={onSkip}
              className="text-sm text-[var(--ps-text-muted)] hover:text-[var(--ps-text-secondary)] underline"
            >
              Having trouble? Skip cropping and upload original
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropModal;
